import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RefreshCw, ShieldOff, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { deleteApiKey, disableApiKey, enableApiKey } from '../api/apiKeys'
import { RotateKeyModal } from './RotateKeyModal'
import { RevokeKeyModal } from './RevokeKeyModal'
import type { ApiKeyResponse } from '../types/apiKey.types'

interface Props {
  accountId: string
  keys: ApiKeyResponse[]
}

type ActiveModal =
  | { type: 'rotate'; key: ApiKeyResponse }
  | { type: 'revoke'; key: ApiKeyResponse }
  | null

function formatDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' })
}

export function ApiKeyTable({ accountId, keys }: Props) {
  const queryClient = useQueryClient()
  const [activeModal, setActiveModal] = useState<ActiveModal>(null)

  const toggleMutation = useMutation({
    mutationFn: ({ keyId, enabled }: { keyId: string; enabled: boolean }) =>
      enabled ? disableApiKey(accountId, keyId) : enableApiKey(accountId, keyId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['api-keys', accountId] }),
  })

  const deleteMutation = useMutation({
    mutationFn: (keyId: string) => deleteApiKey(accountId, keyId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['api-keys', accountId] }),
  })

  const handleDelete = (key: ApiKeyResponse) => {
    if (!window.confirm(`Permanently delete "${key.name}"? This cannot be undone.`)) return
    deleteMutation.mutate(key.keyId)
  }

  if (keys.length === 0) {
    return (
      <div className="text-center py-16 text-gray-500">
        <p className="text-lg font-medium mb-1">No API keys found</p>
        <p className="text-sm">Create a key to get started.</p>
      </div>
    )
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Key Prefix</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Scopes</TableHead>
            <TableHead>Last Used</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {keys.map((key) => {
            const isRevoked = !!key.revokedAt
            return (
              <TableRow key={key.keyId} className={isRevoked ? 'opacity-50' : ''}>
                <TableCell className="font-medium">
                  <div>{key.name}</div>
                  {key.description && (
                    <div className="text-xs text-gray-400 mt-0.5 truncate max-w-[180px]">
                      {key.description}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <code className="text-xs bg-gray-100 rounded px-1.5 py-0.5">{key.keyPrefix}…</code>
                </TableCell>
                <TableCell>
                  <Badge variant={key.keyType === 'LIVE' ? 'destructive' : 'secondary'}>
                    {key.keyType}
                  </Badge>
                </TableCell>
                <TableCell>
                  {isRevoked ? (
                    <Badge variant="outline" className="text-gray-400">Revoked</Badge>
                  ) : key.enabled ? (
                    <Badge variant="success">Active</Badge>
                  ) : (
                    <Badge variant="warning">Disabled</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {key.scopes && key.scopes.length > 0 ? (
                    <span className="text-xs text-gray-600">{key.scopes.length} scope{key.scopes.length !== 1 ? 's' : ''}</span>
                  ) : (
                    <span className="text-xs text-gray-400">Full access</span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-gray-500">{formatDate(key.lastUsedAt)}</TableCell>
                <TableCell className="text-sm text-gray-500">{formatDate(key.createdAt)}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    {!isRevoked && (
                      <>
                        {/* Enable / Disable */}
                        <Button
                          variant="ghost"
                          size="icon"
                          title={key.enabled ? 'Disable key' : 'Enable key'}
                          disabled={toggleMutation.isPending}
                          onClick={() => toggleMutation.mutate({ keyId: key.keyId, enabled: key.enabled })}
                        >
                          {key.enabled
                            ? <ToggleRight className="h-4 w-4 text-green-600" />
                            : <ToggleLeft className="h-4 w-4 text-gray-400" />}
                        </Button>

                        {/* Rotate */}
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Rotate secret"
                          onClick={() => setActiveModal({ type: 'rotate', key })}
                        >
                          <RefreshCw className="h-4 w-4 text-blue-600" />
                        </Button>

                        {/* Revoke */}
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Revoke key"
                          onClick={() => setActiveModal({ type: 'revoke', key })}
                        >
                          <ShieldOff className="h-4 w-4 text-amber-600" />
                        </Button>
                      </>
                    )}

                    {/* Delete (always available) */}
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Delete key"
                      disabled={deleteMutation.isPending}
                      onClick={() => handleDelete(key)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {activeModal?.type === 'rotate' && (
        <RotateKeyModal
          accountId={accountId}
          keyId={activeModal.key.keyId}
          keyName={activeModal.key.name}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal?.type === 'revoke' && (
        <RevokeKeyModal
          accountId={accountId}
          keyId={activeModal.key.keyId}
          keyName={activeModal.key.name}
          onClose={() => setActiveModal(null)}
        />
      )}
    </>
  )
}
